import React from 'react';
import { Box, Typography, Button, Grid, alpha, useTheme } from '@mui/material';
import { Helmet } from 'react-helmet';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import CodeIcon from '@mui/icons-material/Code';
import ExtensionIcon from '@mui/icons-material/Extension';
import PublicIcon from '@mui/icons-material/Public';
import dsaToolkitIcon from '../assets/dsa-toolkit-icon.png';
import dsaToolkitTemplates from '../assets/dsa-toolkit-templates.png';
import dsaToolkitBehavior from '../assets/dsa-toolkit-behavior.png';

const ACCENT = '#137274';

const FEATURES = [
  {
    icon: KeyboardIcon,
    title: 'One shortcut',
    body: 'Ctrl+Shift+P (Cmd+Shift+P on Mac) drops your saved boilerplate straight into the editor — no copy-pasting between tabs.',
  },
  {
    icon: CodeIcon,
    title: '6 languages, fully editable',
    body: 'C++, Java, Python, JavaScript, C# and Go ship with sensible defaults. Edit any of them to match exactly how you like to start a problem.',
  },
  {
    icon: ExtensionIcon,
    title: 'Real editor APIs, not keystroke hacks',
    body: 'Works with Monaco, CodeMirror, Ace, and plain textareas by talking to each editor’s own API directly — reliable on every supported judge.',
  },
  {
    icon: PublicIcon,
    title: 'Works beyond the curated list',
    body: 'Built-in support for LeetCode, Codeforces, GeeksforGeeks, HackerRank, CodeChef, AtCoder and Coding Ninjas — and it can be enabled on any other judge, one click at a time.',
  },
];

const SUPPORTED_SITES = [
  'LeetCode',
  'Codeforces',
  'GeeksforGeeks',
  'HackerRank',
  'CodeChef',
  'AtCoder',
  'Coding Ninjas',
];

const DsaToolkitPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.12)';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  const pageBg = isDark ? '#0b1112' : '#f6f9f9';
  const cardBg = isDark ? '#121a1c' : '#fff';

  return (
    <Box sx={{ minHeight: '100vh', background: pageBg }}>
      <Helmet>
        <title>DSA Toolkit — Instant Boilerplate for Competitive Programming | CrafterHack</title>
        <meta
          name="description"
          content="DSA Toolkit is a free browser extension that instantly inserts your saved code boilerplate into LeetCode, Codeforces, HackerRank and more with a single shortcut."
        />
        <link rel="canonical" href="https://crafterhack.com/dsa-toolkit" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="DSA Toolkit — Instant Boilerplate for Competitive Programming" />
        <meta
          property="og:description"
          content="Free browser extension that instantly inserts your saved code boilerplate into LeetCode, Codeforces, HackerRank and more with a single shortcut."
        />
        <meta property="og:url" content="https://crafterhack.com/dsa-toolkit" />
        <meta property="og:image" content="https://crafterhack.com/dsa-toolkit-og.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://crafterhack.com/dsa-toolkit-og.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'DSA Toolkit',
            applicationCategory: 'DeveloperApplication',
            operatingSystem: 'Chrome, Edge',
            description:
              'Instantly insert your saved code boilerplate into LeetCode, Codeforces, HackerRank and more with a single shortcut.',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
            publisher: { '@type': 'Organization', name: 'CrafterHack', url: 'https://crafterhack.com' },
          })}
        </script>
      </Helmet>

      {/* Hero */}
      <Box
        sx={{
          background: isDark
            ? `radial-gradient(ellipse 80% 50% at 50% 0%, ${alpha(ACCENT, 0.22)}, transparent 60%)`
            : `radial-gradient(ellipse 80% 50% at 50% 0%, ${alpha(ACCENT, 0.1)}, transparent 60%)`,
          pt: { xs: 7, sm: 10 },
          pb: { xs: 5, sm: 7 },
          px: 2.5,
          textAlign: 'center',
        }}
      >
        <Box
          component="img"
          src={dsaToolkitIcon}
          alt=""
          sx={{ width: 72, height: 72, borderRadius: '18px', mb: 2.5, boxShadow: `0 8px 28px ${alpha('#4F7CFF', 0.35)}` }}
        />
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: { xs: '2rem', sm: '2.75rem' },
            letterSpacing: '-0.03em',
            mb: 1.5,
          }}
        >
          DSA Toolkit
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: '1rem', sm: '1.15rem' },
            color: muted,
            maxWidth: 560,
            mx: 'auto',
            mb: 4,
            lineHeight: 1.6,
          }}
        >
          Instantly insert your saved code boilerplate into the editor on LeetCode, Codeforces,
          HackerRank and more — with a single keyboard shortcut. Free, and it never sends your
          code anywhere.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            href="#"
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: ACCENT,
              borderRadius: 2,
              px: 3,
              py: 1.1,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#0f5c5e', boxShadow: 'none' },
            }}
          >
            Add to Chrome — it&apos;s free
          </Button>
          <Button
            href="#"
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              borderRadius: 2,
              px: 3,
              py: 1.1,
              borderColor: border,
              color: theme.palette.text.primary,
            }}
          >
            Add to Edge
          </Button>
        </Box>
      </Box>

      {/* Features */}
      <Box sx={{ maxWidth: 1040, mx: 'auto', px: 2.5, py: { xs: 5, sm: 7 } }}>
        <Grid container spacing={2.5}>
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <Grid item xs={12} sm={6} key={title}>
              <Box
                sx={{
                  height: '100%',
                  bgcolor: cardBg,
                  border: `1px solid ${border}`,
                  borderRadius: 3,
                  p: 3,
                }}
              >
                <Icon sx={{ color: ACCENT, fontSize: 28, mb: 1.5 }} />
                <Typography sx={{ fontWeight: 800, fontSize: '1.02rem', mb: 0.75 }}>
                  {title}
                </Typography>
                <Typography sx={{ color: muted, fontSize: '0.9rem', lineHeight: 1.65 }}>
                  {body}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Screenshots */}
      <Box sx={{ maxWidth: 1040, mx: 'auto', px: 2.5, pb: { xs: 5, sm: 7 } }}>
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <Box
              component="img"
              src={dsaToolkitTemplates}
              alt="DSA Toolkit settings page showing an editable C++ boilerplate template"
              sx={{ width: '100%', borderRadius: 3, border: `1px solid ${border}`, display: 'block' }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box
              component="img"
              src={dsaToolkitBehavior}
              alt="DSA Toolkit settings page showing insertion mode and behavior options"
              sx={{ width: '100%', borderRadius: 3, border: `1px solid ${border}`, display: 'block' }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Supported sites */}
      <Box sx={{ maxWidth: 1040, mx: 'auto', px: 2.5, pb: { xs: 6, sm: 8 }, textAlign: 'center' }}>
        <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', letterSpacing: '.08em', textTransform: 'uppercase', color: muted, mb: 2 }}>
          Built-in support
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
          {SUPPORTED_SITES.map((site) => (
            <Box
              key={site}
              sx={{
                px: 1.75,
                py: 0.6,
                borderRadius: 999,
                border: `1px solid ${border}`,
                bgcolor: cardBg,
                fontSize: '0.85rem',
                fontWeight: 700,
                color: theme.palette.text.primary,
              }}
            >
              {site}
            </Box>
          ))}
        </Box>
        <Typography sx={{ fontSize: '0.8rem', color: muted, mt: 3 }}>
          Works on other judges too — enable it for any site, one click at a time.{' '}
          <Box component="a" href="/dsa-toolkit/privacy" sx={{ color: ACCENT, fontWeight: 700, textDecoration: 'none' }}>
            Privacy Policy
          </Box>
        </Typography>
      </Box>
    </Box>
  );
};

export default DsaToolkitPage;
